import { useState } from 'react';
import { format } from 'date-fns';
import { Loader2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCandidateNotesV2, useAddCandidateNoteV2 } from '@/hooks/useCandidatesV2';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
};

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-red-500',
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

const CandidateNotesTabV2 = ({ candidateId }) => {
  const [noteText, setNoteText] = useState('');

  const { data, isLoading } = useCandidateNotesV2(candidateId);
  const addMutation = useAddCandidateNoteV2();

  const notes = data?.notes || data || [];

  const handleSubmit = () => {
    if (!noteText.trim()) return;
    addMutation.mutate(
      { id: candidateId, data: { content: noteText.trim() } },
      { onSuccess: () => setNoteText('') }
    );
  };

  return (
    <div className="space-y-4">
      {/* Add note */}
      <div className="space-y-2">
        <Textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Write a note..."
          rows={3}
          className="resize-none text-sm"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={handleSubmit}
            disabled={!noteText.trim() || addMutation.isPending}
          >
            {addMutation.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            Add Note
          </Button>
        </div>
      </div>

      {/* Notes list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading notes...
        </div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <MessageSquare className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">No notes yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const authorName = note.createdBy?.name || note.createdBy?.email || 'Unknown';
            return (
              <div key={note._id} className="flex gap-3 text-sm">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ${getAvatarColor(authorName)}`}
                >
                  {getInitials(authorName)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{authorName}</span>
                    <span className="text-xs text-gray-400">
                      {note.createdAt ? format(new Date(note.createdAt), 'MMM dd, yyyy HH:mm') : '—'}
                    </span>
                  </div>
                  <p className="text-gray-700 mt-0.5 whitespace-pre-wrap">{note.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CandidateNotesTabV2;
